import { useState, useEffect } from 'react';
import Select from 'react-select';
import toast from 'react-hot-toast';
import Drawer from '~/components/Drawer';
import { inputStyles, labelStyles, requiredStyles } from './styles';
import { apiUrl, authHeaders } from '~/services/auth';
import { setMedicalProfessionalViaCampaign } from '~/services/campaign';
import MedCenterForm from './MedCenterForm';

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    patient: unknown;
    application?: unknown;
    campaignId?: number; // Campaign ID (preferred, updates Patient directly)
    campaignRequestId?: number; // Campaign Request ID (legacy, for unapproved requests)
    applicationId?: number; // Application ID (for Campaign Application Detail page)
    onUpdate?: (response: any) => void;
}

interface SelectOption {
    value: number | string;
    label: string;
}

const IDENTITY_CHOICES = [
    { value: 1, label: 'Client' },
    { value: 2, label: 'Spouse' },
    { value: 3, label: 'Parent of minor client' },
    { value: 4, label: 'Legal Guardian' },
    { value: 5, label: 'POA' },
];

// Services for searching medical data (using client endpoints)
const searchMedicalCenters = async (state: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/v2/searchMedicalCenters`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ state }),
    });
    if (!resp.ok) throw new Error('Failed to search medical centers');
    return await resp.json();
};

const searchMedicalProfessionals = async (medCenterId: number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/v2/searchMedicalProfessionals`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ medCenterId }),
    });
    if (!resp.ok) throw new Error('Failed to search medical professionals');
    return await resp.json();
};

// API function for Campaign Request endpoint
const setMedicalProfessionalViaCampaignRequest = async (
    medCenterId: number,
    personId: number,
    consent: boolean,
    certifyIdentity: number,
    signature: string,
    campaignRequestId: number,
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/medicalInfo`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
            medCenterId,
            personId,
            consent,
            certifyIdentity,
            signature,
        }),
    });
    if (!resp.ok) throw new Error('Failed to set medical professional');
    return await resp.json();
};

// API function for Campaign Application endpoint
const setMedicalProfessionalViaApplication = async (
    medCenterId: number,
    personId: number,
    consent: boolean,
    certifyIdentity: number,
    signature: string,
    applicationId: number,
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignApplications/${applicationId}/medicalInfo`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
            medCenterId,
            personId,
            consent,
            certifyIdentity,
            signature,
        }),
    });
    if (!resp.ok) throw new Error('Failed to set medical professional');
    return await resp.json();
};

// State options mapping
const US_STATES = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District Of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};

const stateOptions = Object.entries(US_STATES).map(([value, label]) => ({
    value,
    label,
}));

const MedicalInfoDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    patient,
    application,
    campaignId,
    campaignRequestId,
    applicationId,
    onUpdate,
}) => {
    const patientData = patient as {
        medical_professional?: {
            id?: number;
            medical_center?: { id?: number; location?: string; name?: string };
            person?: { id?: number; first_name?: string; last_name?: string };
        };
    };
    const applicationData = application as {
        mp_certify_identity?: number;
        mp_signature?: string;
        mp_give_consent_for_mp_to_provide_info?: boolean;
    };

    const [selectedState, setSelectedState] = useState<SelectOption | null>(
        null,
    );
    const [selectedMedicalCenter, setSelectedMedicalCenter] =
        useState<SelectOption | null>(null);
    const [medicalCenterOptions, setMedicalCenterOptions] = useState<
        SelectOption[]
    >([]);
    const [selectedMedicalProfessional, setSelectedMedicalProfessional] =
        useState<SelectOption | null>(null);
    const [medicalProfessionalOptions, setMedicalProfessionalOptions] =
        useState<SelectOption[]>([]);
    const [consent, setConsent] = useState<boolean>(false);
    const [certifyIdentity, setCertifyIdentity] = useState<number | null>(null);
    const [signature, setSignature] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [showMedCenterForm, setShowMedCenterForm] = useState(false);
    const [showMedProfForm, setShowMedProfForm] = useState(false);

    // Initialize form data from existing patient/application data
    useEffect(() => {
        if (!isOpen) return;

        const medProf = patientData?.medical_professional;
        const medCenter = medProf?.medical_center;

        // Set consent, identity, and signature from application data
        setConsent(
            applicationData?.mp_give_consent_for_mp_to_provide_info || false,
        );
        setCertifyIdentity(applicationData?.mp_certify_identity || null);
        setSignature(applicationData?.mp_signature || '');

        // Initialize state and medical center if available
        if (medCenter?.location) {
            const stateOpt = stateOptions.find(
                (opt) => opt.label === medCenter.location,
            );
            if (stateOpt) {
                setSelectedState(stateOpt);
                // Load medical centers for this state
                loadMedicalCenters(medCenter.location, medCenter.id);
            }
        }
    }, [isOpen, patient, application]);

    const loadMedicalCenters = async (
        state: string,
        preselectedMedCenterId?: number,
    ) => {
        setLoadingOptions(true);
        try {
            const centers = await searchMedicalCenters(state);
            const opts: SelectOption[] = centers.map((center: any) => ({
                label: center.name,
                value: center.id,
            }));
            setMedicalCenterOptions(opts);

            // If we have a preselected medical center, set it
            if (preselectedMedCenterId) {
                const selectedOpt = opts.find(
                    (opt) => opt.value === preselectedMedCenterId,
                );
                if (selectedOpt) {
                    setSelectedMedicalCenter(selectedOpt);
                    // Load medical professionals for this center
                    loadMedicalProfessionals(
                        preselectedMedCenterId,
                        patientData?.medical_professional?.person?.id,
                    );
                }
            }
        } catch (error) {
            console.error('Error loading medical centers:', error);
        } finally {
            setLoadingOptions(false);
        }
    };

    const loadMedicalProfessionals = async (
        medCenterId: number,
        preselectedPersonId?: number,
    ) => {
        setLoadingOptions(true);
        try {
            const professionals = await searchMedicalProfessionals(medCenterId);
            const opts: SelectOption[] = professionals.map((prof: any) => ({
                label: `${prof.first_name} ${prof.last_name}`,
                value: prof.id,
            }));
            setMedicalProfessionalOptions(opts);

            // If we have a preselected professional, set it
            if (preselectedPersonId) {
                const selectedOpt = opts.find(
                    (opt) => opt.value === preselectedPersonId,
                );
                if (selectedOpt) {
                    setSelectedMedicalProfessional(selectedOpt);
                }
            }
        } catch (error) {
            console.error('Error loading medical professionals:', error);
        } finally {
            setLoadingOptions(false);
        }
    };

    const handleStateChange = (option: SelectOption | null) => {
        setSelectedState(option);
        setSelectedMedicalCenter(null);
        setSelectedMedicalProfessional(null);
        setMedicalCenterOptions([]);
        setMedicalProfessionalOptions([]);

        if (option) {
            loadMedicalCenters(option.label);
        }
    };

    const handleMedicalCenterChange = (option: SelectOption | null) => {
        setSelectedMedicalCenter(option);
        setSelectedMedicalProfessional(null);
        setMedicalProfessionalOptions([]);

        if (option && typeof option.value === 'number') {
            loadMedicalProfessionals(option.value);
        }
    };

    const handleMedCenterFormSuccess = (response: any) => {
        setShowMedCenterForm(false);
        // Close the drawer and pass the response to parent for data refresh
        if (onUpdate && response) {
            onUpdate(response);
        }
        setIsOpen(false);
    };

    const handleMedProfFormSuccess = (response: any) => {
        setShowMedProfForm(false);
        // Close the drawer and pass the response to parent for data refresh
        if (onUpdate && response) {
            onUpdate(response);
        }
        setIsOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate that we have a valid ID to save against
        if (!campaignId && !campaignRequestId && !applicationId) {
            console.error(
                'Campaign ID, Campaign Request ID, or Application ID is required to save medical professional data',
            );
            return;
        }

        if (
            !selectedMedicalCenter ||
            !selectedMedicalProfessional ||
            !consent ||
            !certifyIdentity ||
            !signature
        ) {
            toast.error('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        try {
            let response;

            // Prefer campaignId (updates Patient directly, works after approval)
            if (campaignId) {
                response = await setMedicalProfessionalViaCampaign(campaignId, {
                    medCenterId: selectedMedicalCenter.value as number,
                    personId: selectedMedicalProfessional.value as number,
                    consent,
                    certifyIdentity,
                    signature,
                });
            } else if (applicationId) {
                response = await setMedicalProfessionalViaApplication(
                    selectedMedicalCenter.value as number,
                    selectedMedicalProfessional.value as number,
                    consent,
                    certifyIdentity,
                    signature,
                    applicationId,
                );
            } else if (campaignRequestId) {
                response = await setMedicalProfessionalViaCampaignRequest(
                    selectedMedicalCenter.value as number,
                    selectedMedicalProfessional.value as number,
                    consent,
                    certifyIdentity,
                    signature,
                    campaignRequestId,
                );
            }

            if (onUpdate && response) {
                onUpdate(response);
            }

            toast.success('Medical professional info updated');
            setIsOpen(false);
        } catch (error: any) {
            console.error('Error saving medical professional data:', error);
            toast.error(
                error?.message || 'Failed to save medical professional data',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const isValid =
        selectedState &&
        selectedMedicalCenter &&
        selectedMedicalProfessional &&
        consent &&
        certifyIdentity &&
        signature.trim() &&
        !isSubmitting;

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title='Medical Professional Info'
        >
            <form onSubmit={handleSubmit}>
                <label
                    htmlFor='mid-professional-location'
                    className={labelStyles}
                >
                    Where is your Medical Professional located?
                    <span className={requiredStyles}>*</span>
                </label>
                <Select
                    menuPlacement='auto'
                    id='mid-professional-location'
                    options={stateOptions}
                    value={selectedState}
                    onChange={handleStateChange}
                    placeholder='Select a state...'
                    className='mb-[16px]'
                    isDisabled={isSubmitting}
                />

                {selectedState && !showMedCenterForm && !showMedProfForm && (
                    <>
                        <label
                            htmlFor='mid-medical-center'
                            className={labelStyles}
                        >
                            What is your Medical Professional's medical center?
                            <span className={requiredStyles}>*</span>
                        </label>
                        <Select
                            menuPlacement='auto'
                            id='mid-medical-center'
                            options={medicalCenterOptions}
                            value={selectedMedicalCenter}
                            onChange={handleMedicalCenterChange}
                            placeholder='Select a medical center...'
                            className='mb-[16px]'
                            isLoading={loadingOptions}
                            isDisabled={isSubmitting || loadingOptions}
                        />
                        <p className='mb-[16px] text-[14px] text-[#666]'>
                            Is your Medical Center not listed here?{' '}
                            <button
                                type='button'
                                className='text-[#0066CC] underline hover:text-[#0052A3]'
                                onClick={() => setShowMedCenterForm(true)}
                            >
                                Click here to add it.
                            </button>
                        </p>
                    </>
                )}

                {showMedCenterForm && (
                    <MedCenterForm
                        location={selectedState?.label || ''}
                        onCancel={() => setShowMedCenterForm(false)}
                        onSuccess={handleMedCenterFormSuccess}
                        applicationId={applicationId}
                        campaignRequestId={campaignRequestId}
                    />
                )}

                {selectedMedicalCenter &&
                    !showMedCenterForm &&
                    !showMedProfForm && (
                        <>
                            <label
                                htmlFor='mid-professional'
                                className={labelStyles}
                            >
                                Who is your Medical Professional?
                                <span className={requiredStyles}>*</span>
                            </label>
                            <Select
                                menuPlacement='auto'
                                id='mid-professional'
                                options={medicalProfessionalOptions}
                                value={selectedMedicalProfessional}
                                onChange={setSelectedMedicalProfessional}
                                placeholder='Select a medical professional...'
                                className='mb-[16px]'
                                isLoading={loadingOptions}
                                isDisabled={isSubmitting || loadingOptions}
                            />
                            <p className='mb-[16px] text-[14px] text-[#666]'>
                                Is your Medical Professional not listed here?{' '}
                                <button
                                    type='button'
                                    className='text-[#0066CC] underline hover:text-[#0052A3]'
                                    onClick={() => setShowMedProfForm(true)}
                                >
                                    Click here to add it.
                                </button>
                            </p>
                        </>
                    )}

                {showMedProfForm && (
                    <MedCenterForm
                        location={selectedState?.label || ''}
                        selectedMedicalCenter={selectedMedicalCenter}
                        onCancel={() => setShowMedProfForm(false)}
                        onSuccess={handleMedProfFormSuccess}
                        profOnly={true}
                        applicationId={applicationId}
                        campaignRequestId={campaignRequestId}
                    />
                )}

                {selectedMedicalProfessional &&
                    !showMedCenterForm &&
                    !showMedProfForm && (
                        <>
                            <div className='mb-[16px]'>
                                <label className='flex items-start gap-[8px]'>
                                    <input
                                        type='checkbox'
                                        checked={consent}
                                        onChange={(e) =>
                                            setConsent(e.target.checked)
                                        }
                                        disabled={isSubmitting}
                                        className='mt-[4px]'
                                    />
                                    <span className='text-[14px]/[20px]'>
                                        I give consent to their health care
                                        professional to provide information to
                                        Help Hope Live confirming their medical
                                        and financial need for the purpose of
                                        fundraising.
                                        <span className={requiredStyles}>
                                            *
                                        </span>
                                    </span>
                                </label>
                            </div>

                            <label
                                htmlFor='mid-certify-identity'
                                className={labelStyles}
                            >
                                I certify that I am the:
                                <span className={requiredStyles}>*</span>
                            </label>
                            <Select
                                menuPlacement='auto'
                                id='mid-certify-identity'
                                options={IDENTITY_CHOICES}
                                value={IDENTITY_CHOICES.find(
                                    (opt) => opt.value === certifyIdentity,
                                )}
                                onChange={(option) =>
                                    setCertifyIdentity(
                                        option
                                            ? (option.value as number)
                                            : null,
                                    )
                                }
                                placeholder='Select...'
                                className='mb-[16px]'
                                isDisabled={isSubmitting}
                            />

                            <label
                                htmlFor='mid-signature'
                                className={labelStyles}
                            >
                                Name of individual saving these agreements
                                <span className={requiredStyles}>*</span>
                            </label>
                            <input
                                type='text'
                                id='mid-signature'
                                className={`${inputStyles} w-full`}
                                value={signature}
                                onChange={(e) => setSignature(e.target.value)}
                                disabled={isSubmitting}
                            />
                        </>
                    )}

                {!showMedCenterForm && !showMedProfForm && (
                    <div className='mb-[30px] flex gap-[16px]'>
                        <button
                            type='button'
                            className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={() => setIsOpen(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-50'
                            disabled={!isValid}
                        >
                            {isSubmitting ? (
                                <span className='flex items-center justify-center'>
                                    <svg
                                        className='mr-3 h-5 w-5 animate-spin'
                                        viewBox='0 0 24 24'
                                    >
                                        <circle
                                            className='opacity-25'
                                            cx='12'
                                            cy='12'
                                            r='10'
                                            stroke='currentColor'
                                            strokeWidth='4'
                                        />
                                        <path
                                            className='opacity-75'
                                            fill='currentColor'
                                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                        />
                                    </svg>
                                    Saving...
                                </span>
                            ) : (
                                'Save'
                            )}
                        </button>
                    </div>
                )}
            </form>
        </Drawer>
    );
};

export default MedicalInfoDrawer;
